import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { DesCreditPurchasesService } from 'src/app/services/des-credit-purchases.service';

type CreditTier = {
  minimum: number;
  maximum: number | null;
  pricePerThousand: number;
};

@Component({
  selector: 'app-buy-des-credits-page',
  templateUrl: './buy-des-credits-page.component.html',
  styleUrl: './buy-des-credits-page.component.scss',
})
export class BuyDesCreditsPageComponent implements OnInit {
  readonly minimumCredits = 1_000;
  readonly maximumCredits = 5_000_000;
  readonly creditIncrement = 10_000;
  readonly pricingUnit = 1_000;

  readonly tiers: CreditTier[] = [
    { minimum: 1_000, maximum: 250_000, pricePerThousand: 1 },
    { minimum: 250_001, maximum: 2_000_000, pricePerThousand: 0.85 },
    { minimum: 2_000_001, maximum: null, pricePerThousand: 0.65 },
  ];

  credits = 100_000;
  creatingPurchase = false;
  resolvingTarget = false;
  createError = '';
  adminPurchaseDenied = false;
  invalidTargetParams = false;
  targetTenantId = '';
  targetRegistrationId: number | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private desCreditPurchasesService: DesCreditPurchasesService
  ) {}

  ngOnInit(): void {
    const guid = this.route.snapshot.queryParamMap.get('guid')?.trim();
    if (guid) {
      this.resolveEncryptedGuid(guid);
      return;
    }
    const tenantId = this.route.snapshot.queryParamMap.get('tenant_id');
    const registrationId =
      this.route.snapshot.queryParamMap.get('registration_id');
    if (!registrationId) {
      if (tenantId) {
        this.invalidTargetParams = true;
        this.createError = 'tenant_id and registration_id are both required.';
        return;
      }
      this.router.navigate(['/des-credits']);
      return;
    }
    const parsed = Number(registrationId);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      this.invalidTargetParams = true;
      this.createError = 'registration_id is invalid.';
      return;
    }
    this.targetRegistrationId = parsed;
    if (!tenantId) {
      return;
    }
    if (!this.authService.isAdmin()) {
      this.adminPurchaseDenied = true;
      return;
    }
    this.targetTenantId = tenantId;
  }

  private resolveEncryptedGuid(guid: string): void {
    this.resolvingTarget = true;
    this.createError = '';
    this.invalidTargetParams = false;
    this.desCreditPurchasesService.resolveGuid(guid).subscribe({
      next: (res) => {
        this.resolvingTarget = false;
        const registrationId = res.registration_id;
        if (!Number.isInteger(registrationId) || registrationId <= 0) {
          this.invalidTargetParams = true;
          this.createError = 'registration_id is invalid.';
          return;
        }
        this.targetRegistrationId = registrationId;
        this.router.navigate(['/buy-des-credits'], {
          queryParams: { registration_id: registrationId },
          replaceUrl: true,
        });
      },
      error: (err) => {
        this.resolvingTarget = false;
        this.invalidTargetParams = true;
        this.createError = 'Unable to confirm DES credit record.';
      },
    });
  }

  get isAdminPurchase(): boolean {
    return !!this.targetTenantId && this.targetRegistrationId != null;
  }

  get selectedTier(): CreditTier {
    return (
      this.tiers.find(
        (tier) =>
          this.credits >= tier.minimum &&
          (tier.maximum === null || this.credits <= tier.maximum)
      ) ?? this.tiers[0]
    );
  }

  get total(): number {
    return (
      (this.credits / this.pricingUnit) * this.selectedTier.pricePerThousand
    );
  }

  get canContinue(): boolean {
    return (
      !this.resolvingTarget &&
      !this.adminPurchaseDenied &&
      !this.invalidTargetParams &&
      this.targetRegistrationId != null &&
      !this.creatingPurchase &&
      this.credits >= this.minimumCredits &&
      this.credits <= this.maximumCredits
    );
  }

  selectTier(tier: CreditTier): void {
    this.setCredits(tier.minimum);
  }

  stepCredits(direction: 1 | -1): void {
    this.setCredits(this.credits + direction * this.creditIncrement);
  }

  // The slider can only land on its own value grid, so it quantizes to the
  // increment. Typed entry stays exact.
  setCreditsFromSlider(value: string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.setCredits(
      Math.round(parsed / this.creditIncrement) * this.creditIncrement
    );
  }

  setCredits(value: number | string, input?: HTMLInputElement): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      this.credits = this.minimumCredits;
    } else {
      this.credits = Math.min(
        this.maximumCredits,
        Math.max(this.minimumCredits, Math.round(parsed))
      );
    }

    // [value] only writes to the DOM when `credits` changes, so a clamped
    // entry that lands back on the current value would keep its raw text.
    if (input) {
      input.value = String(this.credits);
    }
  }

  continueToCheckout(): void {
    this.setCredits(this.credits);
    if (!this.canContinue) {
      return;
    }
    this.creatingPurchase = true;
    this.createError = '';
    this.desCreditPurchasesService
      .create(
        this.credits,
        this.targetTenantId || undefined,
        this.targetRegistrationId ?? undefined
      )
      .subscribe({
        next: (res) => {
          this.creatingPurchase = false;
          this.router.navigate(['/des-checkout', res.des_credit_purchase.id], {
            queryParams: {
              tenant_id: res.des_credit_purchase.tenant_id,
            },
          });
        },
        error: (err) => {
          this.creatingPurchase = false;
          this.createError =
            'Unable to start checkout. Please try again. If this error persists, please contact support.';
        },
      });
  }
}
